import { RoleName } from '@/api/entities/user-role-type';
import { TCompanySchema } from '@/schemas/company/company.schema';
import { TGroupSchema } from '@/schemas/group/group.schema';
import { TBranchSchema } from '@/schemas/branch/branch.schema';
import { TUserEntityRoleSchema } from '@/schemas/user-entity-role/user-entity-role.schema';
import { QueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';

interface GetRoleAndEntityLabelProps {
  t: TFunction<undefined>;
  roleType: string;
  groups: TGroupSchema[];
  companies: TCompanySchema[];
  branches: TBranchSchema[];
}

export const invalidateUserEntityRoles = (
  queryClient: QueryClient,
  variables: TUserEntityRoleSchema
) => {
  const { userId, roleTypeId } = variables;

  queryClient.invalidateQueries({
    queryKey: ['listAllGroups'],
  });
  queryClient.invalidateQueries({
    queryKey: ['listAllCompanies'],
  });
  queryClient.invalidateQueries({
    queryKey: ['listAllBranches'],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAllUsers'],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAssignedAdmin', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAssignedModerator', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getUnassignedGroupsToUser', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAssignedGroupsToUser', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getUnassignedCompaniesToUser', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAssignedCompaniesToUser', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getUnassignedBranchesToUser', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAssignedBranchesToUser', userId, roleTypeId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAllEntityRolesByUserId', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getEntityRoleByUserId', userId],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAllVehiculeChecks'],
  });
  queryClient.invalidateQueries({
    queryKey: ['getVehiculeCheckById'],
  });
  queryClient.invalidateQueries({
    queryKey: ['getAllEndpointHistories'],
  });
};

export const getPreparedEntityRoleLabel = ({
  t,
  roleType,
  groups,
  companies,
  branches,
}: GetRoleAndEntityLabelProps) => {
  let roleLabel = '';
  let entityLabel = '';
  let fullEntityRoleLabel = '';

  switch (roleType) {
  case RoleName.ADMIN:
    roleLabel = t('common:administrator');
    break;
  case RoleName.MODERATOR:
    roleLabel = t('common:moderator');
    break;
  case RoleName.GROUP_MANAGER:
    roleLabel = t('common:groupManager');
    break;
  case RoleName.COMPANY_MANAGER:
    roleLabel = t('common:companyManager');
    break;
  case RoleName.BRANCH_MANAGER:
    roleLabel = t('common:branchManager');
    break;
  case RoleName.BRANCH_AGENT:
    roleLabel = t('common:branchAgent');
    break;
  }

  const existingGroups = groups?.filter((group) => group?.name);
  const existingCompanies = companies?.filter((company) => company?.name);
  const existingBranches = branches?.filter((branch) => branch?.name);

  if (
    roleType === RoleName.ADMIN ||
    roleType === RoleName.MODERATOR
  ) {
    fullEntityRoleLabel = roleLabel;
  } else if (existingGroups?.length > 0) {
    entityLabel = existingGroups?.map((group) => group?.name)?.join(' / ');
    fullEntityRoleLabel = `${existingGroups?.length > 1 ? `${roleLabel}s` : roleLabel} - ${entityLabel}`;
  } else if (existingCompanies?.length > 0) {
    entityLabel = existingCompanies?.map((company) => company?.name)?.join(' / ');
    fullEntityRoleLabel = `${existingCompanies?.length > 1 ? `${roleLabel}s` : roleLabel} - ${entityLabel}`;
  } else if (existingBranches?.length > 0) {
    entityLabel = existingBranches?.map((branch) => branch?.name)?.join(' / ');
    fullEntityRoleLabel = `${existingBranches?.length > 1 ? `${roleLabel}s` : roleLabel} - ${entityLabel}`;
  }

  return {
    roleLabel,
    entityLabel,
    fullEntityRoleLabel,
  };
};
